import * as React from 'react';
import { Box, Container, Typography, Grid, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TargetIcon from '@mui/icons-material/TrackChanges';
import { DescriptionOutlined, LightbulbOutlined, PeopleAltOutlined, ShieldOutlined, SignalCellularAlt, TaskAltOutlined } from '@mui/icons-material';

const stepsData = [
    {
        number: 'D1',
        title: 'Form the Team',
        description: 'Partner with us to form a quality improvement team. NC Pro helps you collaborate effectively with built-in team management.',
        icon: PeopleAltOutlined,
        gradient: 'linear-gradient(to bottom right, #3B82F6, #2563EB)'
    },
    {
        number: 'D2',
        title: 'Define the Problem',
        description: 'Initiate NCR by defining the problem clearly. Our AI-assisted problem definition ensures accurate documentation from the start.',
        icon: DescriptionOutlined,
        gradient: 'linear-gradient(to bottom right, #6366F1, #4F46E5)'
    },
    {
        number: 'D3',
        title: 'Containment Plan',
        description: 'Develop a robust containment strategy to address defective or rejected products, preventing issue escalation.',
        icon: ShieldOutlined,
        gradient: 'linear-gradient(to bottom right, #A855F7, #9333EA)'
    },
    {
        number: 'D4',
        title: 'Root Cause Analysis',
        description: 'Delve deep with AI-powered analysis. NC Pro identifies escape points and uncovers the true source of issues in minutes.',
        icon: SearchIcon,
        gradient: 'linear-gradient(to bottom right, #8B5CF6, #7C3AED)'
    },
    {
        number: 'D5',
        title: 'Corrective Actions',
        description: 'Formulate permanent corrective actions based on proven data with AI-driven recommendations for innovative solutions.',
        icon: LightbulbOutlined,
        gradient: 'linear-gradient(to bottom right, #E879F9, #D946EF)'
    },
    {
        number: 'D6',
        title: 'Implement Solutions',
        description: 'Execute the corrective measures systematically. NC Pro tracks implementation progress and validates effectiveness.',
        icon: TargetIcon,
        gradient: 'linear-gradient(to bottom right, #F472B6, #EC4899)'
    },
    {
        number: 'D7',
        title: 'Prevent Recurrence',
        description: 'Implement preventive measures across systems. Update procedures, training, and controls to stop future occurrences.',
        icon: TaskAltOutlined,
        gradient: 'linear-gradient(to bottom right, #FB7185, #F43F5E)'
    },
    {
        number: 'D8',
        title: 'Team Recognition',
        description: 'Celebrate your team\'s efforts and successes. NC Pro generates comprehensive reports showcasing achievements and learnings.',
        icon: SignalCellularAlt,
        gradient: 'linear-gradient(to bottom right, #F87171, #EF4444)'
    }
];

export default function Methodology(props) {
    const theme = useTheme();

    return (
        <Box
            component="section"
            id="methodology"
            sx={{
                py: { xs: 8, md: 12 },
                background: 'linear-gradient(to bottom, white, #EFF6FF, white)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 384,
                    height: 384,
                    backgroundColor: '#DBEAFE',
                    borderRadius: '50%',
                    filter: 'blur(48px)',
                    opacity: 0.3,
                    transform: 'translate(50%, -50%)',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: 384,
                    height: 384,
                    backgroundColor: '#EEF2FF',
                    borderRadius: '50%',
                    filter: 'blur(48px)',
                    opacity: 0.3,
                    transform: 'translate(-50%, 50%)',
                }}
            />

            <Container maxWidth="xl" sx={{ px: 3, position: 'relative', zIndex: 10 }}>

                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 10 } }}>
                    <Box sx={{ display: 'inline-block', mb: 2 }}>
                        <Typography
                            component="span"
                            sx={{
                                background: 'linear-gradient(to right, #1E5BB8, #0047AB)',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 999,
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1.5,
                                boxShadow: theme.shadows[3],
                            }}
                        >
                            8D Methodology
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            fontWeight: 700,
                            color: 'gray.900',
                            mb: 3,
                            lineHeight: 1.1,
                        }}
                    >
                        Built on the Industry-Standard{' '}
                        <Box component="span" sx={{
                            background: 'linear-gradient(to right, #1E5BB8, #0047AB)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                        }}>
                            8 Disciplines Process
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            color: 'gray.600',
                            lineHeight: 1.625,
                        }}
                    >
                        NC Pro is fully integrated with the 8D methodology, a globally recognized problem-solving process that systematically addresses non-conformities
                    </Typography>
                </Box>

                <Grid container spacing={3} sx={{ mb: 6 }}>
                    {stepsData.map((step, index) => {
                        const IconComponent = step.icon;
                        return (
                            <Grid item xs={12} sm={6} lg={3} key={index}>
                                <Box
                                    className="group"
                                    sx={{
                                        position: 'relative',
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        p: 3,
                                        border: '2px solid #F3F4F6',
                                        transition: 'all 0.3s',
                                        overflow: 'visible',
                                        height: '100%',

                                        '&:hover': {
                                            borderColor: '#1E5BB8',
                                            boxShadow: theme.shadows[6],
                                            '& .hover-divider': {
                                                width: '100%',
                                            },
                                            '& .icon-box-transition': {
                                                transform: 'scale(1.1)',
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            left: -12,
                                            width: 48,
                                            height: 48,
                                            background: 'linear-gradient(to bottom right, #1E5BB8, #0047AB)',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: theme.shadows[3],
                                            zIndex: 1,
                                        }}
                                    >
                                        <Typography component="span" sx={{ color: 'white', fontWeight: 700, fontSize: '1.125rem' }}>
                                            {step.number}
                                        </Typography>
                                    </Box>

                                    <Box
                                        className="icon-box-transition"
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: '8px',
                                            background: step.gradient,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                            ml: 'auto',
                                            transition: 'transform 0.3s',
                                        }}
                                    >
                                        <IconComponent sx={{ width: 28, height: 28, color: 'white' }} />
                                    </Box>

                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontSize: '1.125rem',
                                            fontWeight: 700,
                                            color: 'gray.900',
                                            mb: 1.5,
                                        }}
                                    >
                                        {step.title}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: '0.875rem',
                                            color: 'gray.600',
                                            lineHeight: 1.625,
                                        }}
                                    >
                                        {step.description}
                                    </Typography>


                                    <Box
                                        className="hover-divider"
                                        sx={{
                                            mt: 2,
                                            height: 4,
                                            width: 0,
                                            background: step.gradient,
                                            borderRadius: 999,
                                            transition: 'width 0.5s',
                                        }}
                                    />
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                <Box sx={{ my: 6, textAlign: 'center' }}>

                </Box>

                <Box
                    sx={{
                        background: 'linear-gradient(to right, #0047AB, #1E5BB8)',
                        borderRadius: '16px',
                        p: { xs: 4, md: 5 },
                        color: 'white',
                        textAlign: 'center',
                        boxShadow: theme.shadows[15],
                    }}
                >
                    <Typography variant="h3" sx={{ fontSize: '1.875rem', fontWeight: 700, mb: 2 }}>
                        Why 8D Methodology Matters
                    </Typography>
                    <Typography
                        sx={{ fontSize: '1.125rem', color: '#93C5FD', mb: 3, maxWidth: 680, mx: 'auto' }}
                    >
                        The 8D process is proven to reduce defects by up to 95%, improve customer satisfaction, and create a culture of continuous improvement. NC Pro automates and accelerates every step.
                    </Typography>
                    <Grid container spacing={3}>

                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(4px)', borderRadius: '8px', p: 3 }}
                            >
                                <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, mb: 1 }}>5 min</Typography>
                                <Typography variant="body2" sx={{ color: '#93C5FD' }}>Complete D4 Root Cause Analysis</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(4px)', borderRadius: '8px', p: 3 }}
                            >
                                <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, mb: 1 }}>1-Click</Typography>
                                <Typography variant="body2" sx={{ color: '#93C5FD' }}>Generate Complete 8D Report</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(4px)', borderRadius: '8px', p: 3 }}
                            >
                                <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, mb: 1 }}>100%</Typography>
                                <Typography variant="body2" sx={{ color: '#93C5FD' }}>Audit-Ready Documentation</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
}
